import { createContext, useContext, useReducer, useEffect, useState, useRef, useMemo, ReactNode, Dispatch } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { initialState, profileReducer } from '../reducer/profileReducer';
import { fetchProfile, normalizeProfileData } from '../services/profileService';
import { updateDraft, updateLastProfile, updatePublish } from '../reducer/profileActions';
import { useToast } from './ToastContext';
import apiClient from '../services/apiService';
import { isValidateDesc, isValidateTitle } from '../utils';

export interface ProfileLink {
  _id?: string;
  id_link: number;
  social_url: string;
  social_name: string;
  icon_name: string;
  icon_id: number | null;
  position?: number;
}

export interface ProfileTheme {
  [key: string]: unknown;
}

export interface ProfileInfo {
  [key: string]: unknown;
}

export interface ProfileData {
  links: ProfileLink[];
  theme: ProfileTheme;
  info: ProfileInfo;
}

export interface ProfileState {
  lastProfile: ProfileData;
  currentProfile: ProfileData;
  isDraft: boolean;
  isPublished: boolean;
  publishDate: string | null;
}

export interface ProfileContextValue {
  state: ProfileState;
  dispatch: Dispatch<{ type: string; payload?: unknown }>;
  loading: boolean;
  error: unknown;
  deploy: () => void;
  pending: boolean;
  hasChanges: boolean;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

const socialMediaSchema = yup.object({
  icon: yup.string().required(),
});

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(profileReducer, initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const cancelled = useRef(false);
  const { t } = useTranslation();
  const { showToast } = useToast();

  useEffect(() => {
    cancelled.current = false;
    fetchProfile()
      .then(response => {
        if (cancelled.current) return;
        if (response.status === 200 && response.data.status === 1) {
          dispatch({ type: 'SET_PROFILE_DATA', payload: normalizeProfileData(response.data.result) });
        } else {
          setError(new Error('profile_load_failed'));
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled.current) return;
        setError(err);
        setLoading(false);
      });
    return () => { cancelled.current = true; };
  }, []);

  const hasChanges = useMemo(
    () => JSON.stringify(state.lastProfile) !== JSON.stringify(state.currentProfile),
    [state.lastProfile, state.currentProfile]
  );

  const deploy = () => {
    const linkSchema = yup.object({
      link: yup.string().url(t('profile.link-string')).required(),
    });

    const statusLink = (link: ProfileLink) => linkSchema.isValidSync({ link: link.social_url });
    const statusSocialMedia = (link: ProfileLink) => socialMediaSchema.isValidSync({ icon: link.icon_id });

    const verify = (profile: ProfileData) => {
      if (!profile.links || profile.links.length === 0)
        return { valid: false, message: t('profile.veryfier.links-length') };
      if (!profile.links.every(statusLink))
        return { valid: false, message: t('profile.veryfier.links-status') };
      if (!profile.links.every(statusSocialMedia))
        return { valid: false, message: t('profile.veryfier.social-media-status') };
      if (!isValidateTitle(profile.info.title))
        return { valid: false, message: t('profile.veryfier.title-length') };
      if (!isValidateDesc(profile.info.description))
        return { valid: false, message: t('profile.veryfier.desc-length') };
      return { valid: true };
    };

    const result = verify(state.currentProfile);
    if (!result.valid) {
      showToast('error', result.message);
      return;
    }

    setPending(true);
    const publishDate = new Date();
    apiClient.patch('/profile/update', {
      links: state.currentProfile.links,
      theme: state.currentProfile.theme,
      info: state.currentProfile.info,
      publishDate,
      isPublished: true,
    })
      .then((response) => {
        const data = response.data;
        if (response.status === 200 && data.status === 1) {
          showToast('success', t('profile.success.published'));
          updatePublish(dispatch, { isPublished: true, publishDate: `${publishDate}` });
          updateLastProfile(dispatch, state.currentProfile);
          updateDraft(dispatch, false);
        } else {
          showToast('error', t('profile.error.internal-error'));
        }
        setPending(false);
      })
      .catch(() => {
        showToast('error', t('profile.error.internal-error'));
        setPending(false);
      });
  };

  return (
    <ProfileContext.Provider value={{ state, dispatch, loading, error, deploy, pending, hasChanges }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextValue => {
  const context = useContext(ProfileContext);
  if (!context) throw new Error('useProfile must be used within a ProfileProvider');
  return context;
};
